"use client"

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { teamActions } from 'features/team/model/slice/teamSlice'
import { MemberRole } from 'features/team/model/types/members'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import _ from 'lodash'
import { avatarApi } from 'shared/api/avatar'
import { request } from 'shared/api/request'
import { userApi } from 'shared/api/userApi'
import { RootState } from 'shared/config/store'
import { USER_DATA_KEY_IN_STORAGE, USER_TOKEN_KEY_IN_STORAGE } from 'shared/constants/user'
import { getFamilySdk } from 'shared/sdk/family'
import { destroyFilesSdk } from 'shared/sdk/files'
import { destroyPhotoSdk } from 'shared/sdk/photo'


import { User, UserState } from '../types/user'

export enum THEME {
  LIGHT = 'light',
  DARK = 'dark',
  SYSTEM = 'system'
}

const getTokenFromStorage = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(USER_TOKEN_KEY_IN_STORAGE)
  }

  return null
}

const getUserDataFromStorage = (): User | null => {
  if (typeof window !== 'undefined') {
    const userData = localStorage.getItem(USER_DATA_KEY_IN_STORAGE)
    return userData ? JSON.parse(userData) : null
  }

  return null
}


function getPreviewAvatarLink(item: any) {
  if (!item?._embedded?.previews?.sizes?.middle) {
    return ''
  }

  const urlTemplate = _.template(item._embedded.previews.url_template, { interpolate: /\{(.+?)\}/g })
  const url = urlTemplate({ size_name: 'middle' }) + `&jwt=${item._embedded.previews.jwt}`

  return url
}

const initialState: UserState = {
  user: getUserDataFromStorage(),
  token: getTokenFromStorage(),
  maxFileSize: Number.MAX_VALUE,
  userSettings: null,
  rootBoardId: '',
  assetRootBoardId: '',
  uiTheme: THEME.LIGHT,
  role: null,
  avatarUrl: null,
  localAvatarUrl: null,
  avatarUploadUrlTmpl: null,
  familyUserData: null
}

// eslint-disable-next-line max-len
export const authThunk = createAsyncThunk('user/auth', async ({ data, successCallback, errorCallback }: { data: { email: string, password: string, remember_me: boolean }, successCallback: () => void, errorCallback: (error: string) => void }, { dispatch }) => {
  try {
    const response = await userApi.login(data)
    destroyPhotoSdk()
    destroyFilesSdk()

    localStorage.setItem(USER_TOKEN_KEY_IN_STORAGE, response.token)
    dispatch(userActions.setToken(response.token))

    await dispatch(fetchUserDataThunk()).unwrap()

    successCallback()

    // TODO: Убрать после нормализации логики
    // window.location.reload()
  } catch (error) {
    console.error('Authorization error:', error)
    return errorCallback(error?.code || 'UnknownError')
  }
})

export const googleAuthThunk = createAsyncThunk(
  "user/googleAuthThunk",
  async ({ jwt_token, state, successCallback }: { jwt_token: string, state, successCallback: () => void }, { dispatch }) => {
    try {
      const response = await userApi.googleAuth({ jwt_token, state })
      if (response.token) {
        localStorage.setItem(USER_TOKEN_KEY_IN_STORAGE, response.token)
        dispatch(userActions.setToken(response.token))

        destroyFilesSdk()
        destroyPhotoSdk()

        await dispatch(fetchUserDataThunk()).unwrap()

        successCallback()
      } else {
        console.error("Google authentication failed: No token received")
      }
    } catch (error) {
      console.error("Google Auth Error:", error)
    }
  }
)

export const createAccountThunk = createAsyncThunk('user/createAccount',
  async ({ data }: { data: { email: string, password: string, name: string } }) => {
    return await userApi.createAccount(data)
  })

export const resendCodeThunk = createAsyncThunk('user/resendCode',
  async ({ email }: { email: string }) => {
    try {
      return await userApi.resendCode({ login: 'email:' + email })
    } catch (error) {
      console.error('Resend code error:', error)
      return error?.code || 'UnknownError'
    }
  })

export const approveSignupThunk = createAsyncThunk('user/approveSignup',
  async ({ hash, successCallback, errorCallback }: { hash: string, successCallback?: () => void, errorCallback?: (error: string) => void }, { dispatch }) => {
    try {
      const response = await userApi.approveSignup(hash)

      localStorage.setItem(USER_TOKEN_KEY_IN_STORAGE, response.token)

      destroyFilesSdk()
      destroyPhotoSdk()

      dispatch(userActions.setToken(response.token))
      await dispatch(fetchUserDataThunk()).unwrap()

      successCallback?.()
    } catch (error) {
      console.log('error approveSignupThunk', error.code, error.hash, error)

      // Обработка специфических ошибок
      let errorMessage = 'Something went wrong, please try again later'

      if (error?.code === 'InvalidParameters' && error?.detail?.hash === 'Hash not found.') {
        errorMessage = 'Invalid code, please check the code or request a new one'
      } else if ( error?.code === 'IncorrectAttemptsLocked') {
        errorMessage = 'Account is locked due to incorrect attempts. Try again after a while'
      }

      return errorCallback?.(errorMessage)
    }
  }
)

export const resendMessageForApproveThunk = createAsyncThunk('user/resendMessageForApproveThunk',
  async ({ login }: { login: string }) => {
    try {
      return await userApi.resendMessageForApprove(login)
    } catch (error) {
      console.error('Resend message for approve error:', error)
      return error?.code || 'UnknownError'
    }
  })

export const ressetPasswordThunk = createAsyncThunk('user/ressetPassword', async ({ login, successCallback }: { login: string, successCallback: () => void }) => {
  try {
    await userApi.ressetPassword(login)
    successCallback()
  } catch (error) {
    console.error('Resset password error:', error)
  }
})

export const fetchUserDataThunk = createAsyncThunk('user/fetchUserData', async (_, { getState, dispatch }) => {
  try {
    const state = getState() as RootState
    const token = state.user.token

    if (token) {
      const userLinks = await userApi.getUserLinks()
      const userLink = userLinks?._links?.current_user?.href
      
      if (!userLink) {
        localStorage.clear()

        localStorage.removeItem(USER_TOKEN_KEY_IN_STORAGE)

        dispatch(userActions.clearUserData())

        return
      }

      const data = await request<any>('GET', userLink, {}, { host: null })
      const familiesResponse = await request<any>('GET', data._links.families.href, { limit: 1 }, { host: null })
      const familyData = familiesResponse._embedded.families?.[0]

      if (familyData) {
        const sdk = getFamilySdk()
        const { data: familyMembersData } = await sdk.getFamilyMembers(familyData.id, { limit: 1000 })
        const familyUserData = await userApi.getUserDataById(familyData.shared_user_id) as User
        const familyMembers = familyMembersData?._embedded?.members || []
        
        dispatch(teamActions.setAllMembers(familyMembers))
        dispatch(userActions.setFamilyUserData(familyUserData))
        
        const userRole = familyMembers.find(member => member.user_id == data.id)?.role

        dispatch(userActions.setUserRole(userRole as MemberRole))
      }

      const familyUserId = familyData?.shared_user_id
      const familyOwnerId = familyData?.owner_id

      const userData = { ...data, userid: data.id, user_id: data.id, family_user_id: familyUserId, is_owner_family: familyUserId ? data.id === familyOwnerId : null }

      dispatch(userActions.setUserData(userData))
      dispatch(teamActions.setFamilyData(familyData))
      localStorage.setItem(USER_DATA_KEY_IN_STORAGE, JSON.stringify(userData))
    }
  } catch (error: any) {
    console.error('Ошибка при загрузке данных пользователя:', error)

    // Treat only explicit auth errors as fatal, keep session for other failures
    const isAuthError = error?.status === 401 || error?.code === 'Unauthorized'

    if (isAuthError) {
      localStorage.removeItem(USER_TOKEN_KEY_IN_STORAGE)
      dispatch(userActions.clearUserData())
    } else {
      // Surface a user-facing message without tearing down the session
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Ошибка',
        message: 'Не удалось загрузить данные профиля. Пожалуйста, попробуйте позже.'
      })
    }
  }
})

export const logoutThunk = createAsyncThunk(
  'user/logout',
  async function (_, { getState, dispatch }) {
    const state = getState() as RootState
    const token = state.user.token
    const userId = state.user.user?.id

    try {
      if (!token || !userId) return
      await userApi.logout(token, userId)
    } finally {
      localStorage.clear()
      sessionStorage.clear()
      window.location.href = '/'
    }
  }
)

export const fetchUserSettingsThunk = createAsyncThunk('user/fetchUserSettings', async (_, { getState, dispatch }) => {
  try {
    const state = getState() as { user: UserState }
    const familyUserId = state.user.user?.family_user_id
    if (familyUserId) {
      return await userApi.getUserSettings(familyUserId)
    }
  } catch (error) {
    console.error('Ошибка при загрузке настроек пользователя:', error)
  }
})

export const deleteAvatarThunk = createAsyncThunk('user/deleteAvatar', async ({ signal }: { signal: AbortSignal }, { getState, dispatch }) => {
  const state = getState() as RootState
  const userId = state.user.user?.id
  if (userId) {
    return await avatarApi.deleteAvatar(userId, signal)
  }
})

export const updateUserDataThunk = createAsyncThunk(
  'user/updateUserDataThunk',
  async function (data: any, { dispatch }) {
    const userId = data.id
    const { name } = data

    await userApi.updateUserData(userId, { name })

    const userDataFromLocalStorage = localStorage.getItem(USER_DATA_KEY_IN_STORAGE)

    const parsedDataFromLocalStorage = JSON.parse(userDataFromLocalStorage)

    const updatedData = { ...parsedDataFromLocalStorage, ...data }

    localStorage.setItem(USER_DATA_KEY_IN_STORAGE, JSON.stringify(updatedData))

    dispatch(userActions.setUserData(updatedData))
  }
)

export const changePasswordThunk = createAsyncThunk(
  'user/changePasswordThunk',
  async function (data: { userId: number, password: string, current_password: string }, { rejectWithValue }) {
    try {
      await userApi.changePassword(data.userId, {
        password: data.password,
        current_password: data.current_password
      })
    } catch (error: any) {
      return rejectWithValue(error)
    }
  }
)

export const fetchUserAvatarThunk = createAsyncThunk('user/fetchUserAvatar', async (_, { getState, dispatch }) => {
  const state = getState() as RootState
  const userId = state.user.user?.id
  try {
    if (userId) {
      const avatar = await avatarApi.getAvatarResource(userId)
      const avatarUrl = getPreviewAvatarLink(avatar)
      dispatch(userActions.setAvatarUrl(avatarUrl))
    }
  } catch (e) {
    dispatch(userActions.setAvatarUrl(null))
  }
})


export const fetchUserAvatarsThunk = createAsyncThunk('user/fetchUserAvatars', async (_, { getState, dispatch }) => {
  try {
    const state = getState() as RootState
    const token = state.user.token
    const userId = state.user.user?.id
    if (token && userId) {
      const avatars = await avatarApi.getAvatars(userId)
      const uploadUrlTmpl = avatars._links?.upload_avatar?.href || ''
      dispatch(userActions.setAvatarUploadUrlTmpl(uploadUrlTmpl))
    }
  } catch (error) {
    console.error('Ошибка при загрузке аватаров пользователя:', error)
  }
})

export const uploadAvatarThunk = createAsyncThunk('user/uploadAvatar', async (file: File, { getState, dispatch }) => {
  const state = getState() as RootState
  const token = state.user.token
  const avatarUploadUrlTmpl = state.user.avatarUploadUrlTmpl
  await avatarApi.uploadAvatarByUrl(avatarUploadUrlTmpl, file, token)
  dispatch(userActions.setLocalAvatarUrl(null))
  dispatch(fetchUserAvatarThunk())
})

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload
    },
    setUserData: (state, action: PayloadAction<User>) => {
      state.user = action.payload
    },
    clearUserData: (state) => {
      state.user = null
      state.token = null
    },
    setUserSettings: (state, action: PayloadAction<any>) => {
      state.userSettings = action.payload
    },
    setDefaultRoots: (state, action: PayloadAction<{ rootBoardId: string, assetRootBoardId: string }>) => {
      state.rootBoardId = action.payload.rootBoardId
      state.assetRootBoardId = action.payload.assetRootBoardId
    },
    setUserRole: (state, action: PayloadAction<MemberRole>) => {
      state.role = action.payload
    },
    setAvatarUrl: (state, action: PayloadAction<string>) => {
      state.avatarUrl = action.payload
    },
    setLocalAvatarUrl: (state, action: PayloadAction<string>) => {
      state.localAvatarUrl = action.payload
    },
    setAvatarUploadUrlTmpl: (state, action: PayloadAction<string>) => {
      state.avatarUploadUrlTmpl = action.payload
    },
    setFamilyUserData: (state, action: PayloadAction<User>) => {
      state.familyUserData = action.payload
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchUserSettingsThunk.fulfilled, (state, action) => {
      state.userSettings = action.payload?._embedded?.values
    })
  }
})

export const userActions = userSlice.actions

export default userSlice.reducer
